import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { CarPreview } from '../components/Car3DViewer';
import SocialProofBar from '../components/SocialProofBar';
import { CampaignVideoSection } from '../components/CampaignVideoSection';
import { useAlternatingVideoPlayback } from '../hooks/useAlternatingVideoPlayback';

const Home = () => {
  const videoGroup = useRef(null);
  useAlternatingVideoPlayback(videoGroup);

  return (
    <div className="bg-cream-light" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy leading-tight">
              Join the Group,
              <span className="text-gold"> Advectigroup</span>
            </h1>
            <p className="mt-6 text-xl text-navy/70">
              Drivers earn money. Businesses get seen. Advertising on cars 
              with wraps, only in Columbus.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/get-started"
                className="inline-flex items-center justify-center gap-2 bg-navy text-cream px-8 py-4 rounded-full text-lg font-semibold hover:bg-navy-dark transition-all pulse-cta"
                data-testid="hero-cta-start"
              >
                Get Started
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy px-8 py-4 rounded-full text-lg font-semibold hover:bg-navy hover:text-cream transition-all"
                data-testid="hero-cta-learn"
              >
                Learn How It Works
              </Link>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 bg-navy/10 px-6 py-3 rounded-full">
              <span className="text-navy font-bold text-2xl">Up to $8/hour</span>
              <span className="text-navy/70">for drivers</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-light" data-testid="ad-videos-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={videoGroup} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="ad-videos-grid">
            <CampaignVideoSection
              id="home-campaign-video"
              label="Advecti campaign video"
              src="https://customer-assets.emergentagent.com/job_489873ad-582b-4a32-a22f-601ab3398056/artifacts/m5hivado_Advecti_ad_v9.mp4"
              fallbackSrc="/videos/advecti-home-campaign.webm"
              poster="/advecti-home-video-poster.jpg"
            />
            <CampaignVideoSection
              id="home-advertiser-campaign-video"
              label="Advecti advertiser campaign video"
              src="https://customer-assets.emergentagent.com/job_489873ad-582b-4a32-a22f-601ab3398056/artifacts/lcm6c8x2_Advecti_ad2_noUber.mp4"
              fallbackSrc="/videos/advecti-advertiser-campaign.webm"
              poster="/advecti-advertiser-video-poster.jpg"
            />
          </div>
        </div>
      </section>

      {/* Live social-proof bar (real-time counters from the live DB) */}
      <SocialProofBar />

      {/* Wrap Type Previews */}
      <section className="py-16 bg-white" data-testid="wrap-previews-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-navy text-center mb-4">Our Wrap Options</h2>
          <p className="text-navy/70 text-center mb-10">Choose the coverage that fits your brand</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <CarPreview type="full_wrap" />
              <div className="text-center mt-3">
                <h3 className="text-xl font-bold text-navy">Full Wrap</h3>
                <p className="text-navy/60 text-sm mt-1">Entire vehicle covered in your branded design for maximum visibility.</p>
              </div>
            </div>
            <div>
              <CarPreview type="partial_wrap" />
              <div className="text-center mt-3">
                <h3 className="text-xl font-bold text-navy">Partial Wrap</h3>
                <p className="text-navy/60 text-sm mt-1">Select panels wrapped with your branding — eye-catching and cost-effective.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serving Columbus Section */}
      <section className="py-16 bg-cream" data-testid="serving-columbus-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-navy mb-4">Serving Columbus, Ohio</h2>
          <p className="text-xl text-navy/70 mb-8">
            Advecti operates exclusively in the Columbus metropolitan area.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div className="flex items-center justify-center gap-4 mb-4">
              <MapPin className="text-navy" size={32} />
              <span className="text-2xl font-bold text-navy">Columbus Metro Area</span>
            </div>
            <p className="text-navy/70">
              Including Downtown Columbus, Dublin, Westerville, Grove City, Hilliard, 
              Reynoldsburg, Gahanna, and surrounding communities.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-cream mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-cream mb-8">
            Whether you want to earn extra income or advertise your business, 
            we're here to help.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center justify-center gap-3 bg-cream text-navy px-10 py-5 rounded-full text-xl font-bold hover:bg-cream-light transition-all pulse-cta"
            data-testid="cta-final"
          >
            Get Started Today
          </Link>
          <p className="mt-6 text-cream">
            <MapPin className="inline mr-2" size={16} />
            Serving the Columbus Metropolitan Area
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
